import { useEffect, useState } from 'react';

import { useMediaQuery } from 'src/shared/hooks';
import { Rfq } from 'src/shared/types/rfq';
import { UploadedFile } from 'src/shared/types/uploadedFile';

import { cn, ModalContent } from 'src/shared/components/ui';
import { Modal, ModalBody, Typography } from 'src/shared/components/ui';

import AddReferencesImageTab from './AddReferencesImageTab';
import AddReferencesUploadTab from './AddReferencesUploadTab';

type TabKey = 'references' | 'upload';

interface AddReferencesModalProps {
  isOpen: boolean;
  onClose: () => void;
  rfq?: Rfq;
  /** Callback when files are selected from either tab */
  onFilesSelected?: (files: UploadedFile[]) => void;
  /**
   * Optional files to seed into the Upload tab when the modal opens (e.g. from
   * a window-level drag-and-drop). Consumed once and then cleared by the parent
   * via {@link onInitialFilesConsumed}.
   */
  initialFiles?: File[];
  /** Called after `initialFiles` have been seeded so the parent can clear them. */
  onInitialFilesConsumed?: () => void;
  /** Which tab to open by default. Defaults to "upload". */
  defaultTab?: TabKey;
}

const TABS: { key: TabKey; label: string }[] = [
  { key: 'upload', label: 'Upload Files' },
  { key: 'references', label: 'Reference Images' }
];

/**
 * Modal that combines reference image search and file upload into a tabbed interface.
 * Used from the RFQ builder chat to allow users to add reference images or upload files.
 */
const AddReferencesModal = ({
  isOpen,
  onClose,
  rfq,
  onFilesSelected,
  initialFiles,
  onInitialFilesConsumed,
  defaultTab = 'upload'
}: AddReferencesModalProps) => {
  const [activeTab, setActiveTab] = useState<TabKey>(defaultTab);

  // Force the upload tab when files are seeded mid-session (e.g. user drops more
  // files while the modal is open on the references tab).
  useEffect(() => {
    if (initialFiles && initialFiles.length > 0) {
      setActiveTab('upload');
    }
  }, [initialFiles]);

  const handleFilesSelected = (files: UploadedFile[]) => {
    onFilesSelected?.(files);
    onClose();
  };

  const isMobile = useMediaQuery();

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      size={isMobile ? 'full' : 'xl'}
      scrollBehavior="inside"
      className="!min-w-full md:!min-w-[80%] overflow-hidden h-full !m-0 md:!m-auto !rounded-none md:!rounded-lg"
    >
      <ModalContent>
        <ModalBody className="p-0 min-h-[100dvh] md:min-h-[90vh]">
          <div className="flex flex-col h-full max-h-[100dvh] md:max-h-[90vh]">
            {/* Fixed tab navigation header */}
            <div className="sticky top-0 z-40 bg-white flex items-center gap-4 md:gap-6 px-4 md:px-6 pt-4 md:pt-6 pb-2 border-b border-gray-3 flex-shrink-0">
              {TABS.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={cn(
                    'pb-3 transition-colors relative',
                    activeTab === tab.key
                      ? 'text-primary'
                      : 'text-secondary-text hover:text-dark'
                  )}
                >
                  <Typography
                    variant="body-1"
                    weight={activeTab === tab.key ? 'bold' : 'medium'}
                  >
                    {tab.label}
                  </Typography>
                  {activeTab === tab.key && (
                    <div className="absolute bottom-[-8px] left-0 right-0 h-[3px] bg-primary rounded-t-full" />
                  )}
                </button>
              ))}
            </div>

            {/* Tab content - both panels stay mounted to preserve state */}
            <div className="flex-1 min-h-0">
              <div
                className={cn(
                  'h-full',
                  activeTab === 'references' ? 'block' : 'hidden'
                )}
              >
                <AddReferencesImageTab
                  isActive={isOpen && activeTab === 'references'}
                  rfq={rfq}
                  onFilesSelected={handleFilesSelected}
                  onClose={onClose}
                />
              </div>
              <div
                className={cn(
                  'h-full',
                  activeTab === 'upload' ? 'block' : 'hidden'
                )}
              >
                <AddReferencesUploadTab
                  rfq={rfq}
                  onFilesSelected={handleFilesSelected}
                  onClose={onClose}
                  initialFiles={initialFiles}
                  onInitialFilesConsumed={onInitialFilesConsumed}
                />
              </div>
            </div>
          </div>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default AddReferencesModal;
